import { action, observable } from 'mobx'
import {
  type IValidationSchema,
  type IValidator,
  required,
  validationContext,
} from 'validx'
import { Types } from '@/features/legends/editor/ryno-types'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { DateTimeQuestion } from '@/features/legends/questions/questions'

const onlyOneDirection: IValidator<DateTimeQuestionEditorViewModel> = ({
  obj,
}) =>
  obj.disallowFuture && obj.disallowPast
    ? 'Can only select future or past.'
    : true

/**
 * A question answered with a date, and optionally a time.
 */
export class DateTimeQuestionEditorViewModel extends QuestionEditorViewModelBase<DateTimeQuestion> {
  private readonly _schema: IValidationSchema<DateTimeQuestionEditorViewModel> =
    {
      ...QuestionEditorViewModelBase.sharedSchema,
      question: [required('Question is required')],
      disallowPast: [onlyOneDirection],
      disallowFuture: [onlyOneDirection],
    }

  readonly type = 'DateTime'

  @observable accessor question = 'New Date & Time Question'
  @observable accessor description = ''
  /**
   * Whether the client picks a time too.
   */
  @observable accessor collectTime = false

  /**
   * Whether only future dates can be picked.
   */
  @observable accessor disallowPast = false

  /**
   * Whether only past dates can be picked.
   */
  @observable accessor disallowFuture = false

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this._validateWhileEditing(this._schema, {
      disallowPast: ['disallowFuture'],
      disallowFuture: ['disallowPast'],
    })
  }

  /** @inheritDoc */
  get valueType() {
    return Types.date
  }

  /** @inheritDoc */
  // eslint-disable-next-line @typescript-eslint/class-literal-property-style -- overrides a getter
  get answerOptions() {
    return null
  }

  /** @inheritDoc */
  get displayText() {
    return this.question
  }

  /**
   * Sets whether the client is asked for a time too.
   */
  @action.bound
  setCollectTime(value: boolean) {
    this.collectTime = value
  }

  /**
   * Sets whether only future dates can be picked.
   */
  @action.bound
  setDisallowPast(value: boolean) {
    this.disallowPast = value
  }

  /**
   * Sets whether only past dates can be picked.
   */
  @action.bound
  setDisallowFuture(value: boolean) {
    this.disallowFuture = value
  }

  /** @inheritDoc */
  @action
  hydrate(question: DateTimeQuestion) {
    this._hydrateShared(question)
    this.question = question.question ?? this.question
    this.description = question.description ?? ''
    this.collectTime = !!question.collectTime
    this.disallowPast = !!question.disallowPast
    this.disallowFuture = !!question.disallowFuture
  }

  /** @inheritDoc */
  protected _serialize(): DateTimeQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      question: this.question,
      description: this.description,
      collectTime: this.collectTime,
      disallowPast: this.disallowPast,
      disallowFuture: this.disallowFuture,
    }
  }
}
